import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { StandardEquipmentCatalogue } from "../../../configurator";
import type {
  MeasurementLogFormValues,
  MeasurementRecord,
  SavedCoilConfiguration,
} from "../../../web-app";
import { buildMeasurementLogErrors } from "../../../web-app";
import {
  buildMeasurementRecord,
  emptyMeasurementFormValues,
  measurementToFormValues,
} from "../domain/measurementModal";
import "./measurement-log-modal.css";
import CcButton from "../components/CcButton";

export interface MeasurementLogModalProps {
  open: boolean;
  mode: "create" | "edit";
  editRecord: MeasurementRecord | null;
  configurations: SavedCoilConfiguration[];
  catalogue: StandardEquipmentCatalogue;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (record: MeasurementRecord) => void;
}

const MeasurementLogModal = ({
  open,
  mode,
  editRecord,
  configurations,
  catalogue,
  submitting,
  onClose,
  onSubmit,
}: MeasurementLogModalProps) => {
  const [values, setValues] = useState<MeasurementLogFormValues>(emptyMeasurementFormValues);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setFieldErrors({});
    if (mode === "edit" && editRecord) {
      setValues(measurementToFormValues(editRecord));
      return;
    }
    setValues(emptyMeasurementFormValues());
  }, [open, mode, editRecord]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, submitting]);

  const configurationOptions = useMemo(() => {
    const options = configurations.map((config) => ({
      value: config.id,
      label: config.reference,
    }));

    if (
      editRecord !== null &&
      !configurations.some((config) => config.id === editRecord.configurationId)
    ) {
      options.push({
        value: editRecord.configurationId,
        label: `${editRecord.configurationReference} (existing record)`,
      });
    }

    return options;
  }, [configurations, editRecord]);

  const setField = useCallback(
    <K extends keyof MeasurementLogFormValues>(key: K, value: MeasurementLogFormValues[K]) => {
      setValues((prev) => ({ ...prev, [key]: value }));
      setFieldErrors((prev) => {
        const next = { ...prev };
        const errorKey = FIELD_ERROR_KEYS[key];
        if (errorKey) delete next[errorKey];
        return next;
      });
    },
    [],
  );

  const handleSubmit = () => {
    if (submitting) return;
    const errors = buildMeasurementLogErrors(values);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const record = buildMeasurementRecord(
      values,
      configurations,
      catalogue,
      editRecord ?? undefined,
    );
    if (record === null) return;
    onSubmit(record);
  };

  if (!open) return null;

  const title = mode === "edit" ? "Edit Measurement" : "Log Measurement";
  const submitLabel = mode === "edit" ? "Save Measurement" : "Log Measurement";

  return (
    <div
      className="dialog-scrim"
      role="presentation"
      onClick={(event) => {
        if (submitting) return;
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="dialog-card measurement-log-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="measurement-log-modal-title"
      >
        <div className="dialog-header measurement-log-modal__header">
          <h2 className="dialog-title" id="measurement-log-modal-title">
            {title}
          </h2>
          <CcButton variant="icon" aria-label="Close modal" onClick={onClose} disabled={submitting}>
            ✕
          </CcButton>
        </div>

        <div className="dialog-body measurement-log-modal-body">
          <form
            id="measurement-log-modal-form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              handleSubmit();
            }}
          >
            <div className="measurement-log-form-grid">
              <div className="measurement-log-form-field measurement-log-form-field--full">
                <label htmlFor="log-config" className="add-catalogue-field__label">
                  Configuration
                </label>
                <select
                  id="log-config"
                  className="add-catalogue-field__input"
                  value={values.configurationId}
                  onChange={(event) => setField("configurationId", event.target.value)}
                >
                  <option value="">— Select a configuration —</option>
                  {configurationOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {fieldErrors["log-config"] && (
                  <span className="field-error" role="alert">
                    {fieldErrors["log-config"]}
                  </span>
                )}
              </div>

              <NumericField
                id="log-voltage"
                label="Power Source Voltage, V"
                step="0.1"
                value={values.powerSourceVoltageV}
                error={fieldErrors["log-voltage"]}
                onChange={(value) => setField("powerSourceVoltageV", value)}
              />
              <NumericField
                id="log-current"
                label="Power Source Current, A"
                step="0.1"
                value={values.powerSourceCurrentA}
                error={fieldErrors["log-current"]}
                onChange={(value) => setField("powerSourceCurrentA", value)}
              />
              <NumericField
                id="log-power-factor"
                label="Power Factor"
                step="0.001"
                value={values.powerFactor}
                error={fieldErrors["log-power-factor"]}
                onChange={(value) => setField("powerFactor", value)}
              />
              <NumericField
                id="log-measured-temp"
                label="Measured Temperature Reached, °C"
                step="0.1"
                value={values.measuredTemperatureC}
                error={fieldErrors["log-measured-temp"]}
                onChange={(value) => setField("measuredTemperatureC", value)}
              />
              <NumericField
                id="log-meas-cycle-time"
                label="Measured Heating Cycle Time, min"
                step="0.1"
                value={values.measuredHeatingCycleTimeMinutes}
                error={fieldErrors["log-meas-cycle-time"]}
                onChange={(value) => setField("measuredHeatingCycleTimeMinutes", value)}
              />
              <NumericField
                id="log-meas-cooldown-time"
                label="Measured Cooldown Time, min"
                step="0.1"
                value={values.measuredCooldownTimeMinutes}
                onChange={(value) => setField("measuredCooldownTimeMinutes", value)}
              />

              <div className="measurement-log-form-field measurement-log-form-field--full">
                <label htmlFor="log-notes" className="add-catalogue-field__label">
                  Notes
                </label>
                <textarea
                  id="log-notes"
                  className="add-catalogue-field__input"
                  rows={3}
                  value={values.notes}
                  onChange={(event) => setField("notes", event.target.value)}
                />
              </div>
            </div>
          </form>
        </div>

        <div className="dialog-footer">
          <CcButton variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </CcButton>
          <CcButton
            variant="primary"
            className="measurement-log-modal__submit"
            type="submit"
            form="measurement-log-modal-form"
            loading={submitting}
            disabled={submitting}
          >
            {submitLabel}
          </CcButton>
        </div>
      </div>
    </div>
  );
};

const FIELD_ERROR_KEYS: Partial<Record<keyof MeasurementLogFormValues, string>> = {
  configurationId: "log-config",
  powerSourceVoltageV: "log-voltage",
  powerSourceCurrentA: "log-current",
  powerFactor: "log-power-factor",
  measuredTemperatureC: "log-measured-temp",
  measuredHeatingCycleTimeMinutes: "log-meas-cycle-time",
};

interface NumericFieldProps {
  id: string;
  label: string;
  step: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}

function NumericField({ id, label, step, value, error, onChange }: NumericFieldProps) {
  return (
    <div className="measurement-log-form-field">
      <label htmlFor={id} className="add-catalogue-field__label">
        {label}
      </label>
      <input
        type="number"
        id={id}
        className="add-catalogue-field__input pih-numeric-input"
        step={step}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export default MeasurementLogModal;
